import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Snackbar } from "@mui/material";
import NotificationsRoundedIcon from "@mui/icons-material/NotificationsRounded";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { API_ENDPOINT } from "../../../services/api/resources";
import {
  clearNotifications,
  prependNotification,
  setNotifications,
} from "../../../features/NotificationSlice";

const getScope = (loginData) => {
  const type = String(loginData?.access_type || "").toLowerCase();
  if (type === "buyer" || type === "admin") {
    return { key: "buyer_token", value: loginData?.user?.token || loginData?.buyer_token || loginData?.organization_token };
  }
  if (type === "user") {
    return { key: "user_token", value: loginData?.user?.user_token || loginData?.user_token || loginData?.user?.token };
  }
  if (type === "vendor admin" || type === "vendor contact") {
    return { key: "vendor_token", value: loginData?.user?.token || loginData?.vendor_contact_token || loginData?.vendor_token };
  }
  return null;
};

const getSocketUrl = (scope) => {
  const apiUrl = new URL(API_ENDPOINT.Base_URL);
  apiUrl.protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  apiUrl.pathname = "/ws/notifications/";
  apiUrl.search = new URLSearchParams({ [scope.key]: scope.value }).toString();
  return apiUrl.toString();
};

export const useNotificationSocket = () => {
  const dispatch = useDispatch();
  const loginData = useSelector((state) => state.loginData.value);
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const [toastMessage, setToastMessage] = useState("");
  const reconnectTimerRef = useRef(null);
  const socketRef = useRef(null);
  const notificationIdsRef = useRef(new Set());

  const authToken = loginData?.auth_token || loginData?.token || "";
  const scope = useMemo(() => getScope(loginData), [loginData]);

  useEffect(() => {
    let stopped = false;
    let attempts = 0;

    const closeSocket = () => {
      window.clearTimeout(reconnectTimerRef.current);
      socketRef.current?.close();
      socketRef.current = null;
    };

    if (!isLoggedIn || !authToken || !scope?.value) {
      closeSocket();
      dispatch(clearNotifications());
      return closeSocket;
    }

    const fetchHistory = async () => {
      const formData = new FormData();
      formData.append(scope.key, scope.value);
      try {
        const response = await axios.post(API_ENDPOINT.getNotifications, formData, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (!stopped) {
          const notifications = (response?.data?.response || []).filter((item) => item?.message);
          notificationIdsRef.current = new Set(notifications.map((item) => String(item.id)));
          dispatch(setNotifications(notifications));
        }
      } catch {
        if (!stopped) dispatch(setNotifications([]));
      }
    };

    const connect = () => {
      if (stopped) return;
      const socket = new WebSocket(getSocketUrl(scope));
      socketRef.current = socket;
      socket.onopen = () => { attempts = 0; };
      socket.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);
          if (payload?.event !== "notification.created" || !payload?.notification?.id) return;
          const notificationId = String(payload.notification.id);
          if (notificationIdsRef.current.has(notificationId)) return;
          notificationIdsRef.current.add(notificationId);
          dispatch(prependNotification(payload.notification));
          if (payload.notification.status === false) setToastMessage(payload.notification.message || "New notification");
        } catch { /* Ignore malformed WebSocket payloads. */ }
      };
      socket.onclose = () => {
        if (stopped) return;
        const delay = Math.min(1000 * 2 ** attempts, 30000);
        attempts += 1;
        reconnectTimerRef.current = window.setTimeout(connect, delay);
      };
    };

    fetchHistory();
    connect();
    return () => {
      stopped = true;
      closeSocket();
    };
  }, [authToken, dispatch, isLoggedIn, scope]);

  return { toastMessage, closeToast: () => setToastMessage("") };
};

const NotificationRealtime = () => {
  const { toastMessage, closeToast } = useNotificationSocket();
  return (
    <Snackbar
      open={Boolean(toastMessage)}
      autoHideDuration={6500}
      onClose={closeToast}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      sx={{ top: { xs: 16, sm: 24 }, right: { xs: 12, sm: 24 } }}
    >
      <Alert
        icon={<NotificationsRoundedIcon fontSize="inherit" />}
        severity="info"
        variant="filled"
        onClose={closeToast}
        sx={{
          alignItems: "flex-start",
          minWidth: { xs: "calc(100vw - 24px)", sm: 400 },
          maxWidth: 520,
          borderRadius: 3,
          px: 1.5,
          py: 1.25,
          color: "#172B4D",
          background: "linear-gradient(135deg, #FFFFFF 0%, #EEF5FF 100%)",
          border: "1px solid rgba(21, 94, 239, 0.2)",
          borderLeft: "4px solid #155EEF",
          boxShadow: "0 18px 45px rgba(15, 23, 42, 0.18), 0 2px 8px rgba(21, 94, 239, 0.1)",
          "& .MuiAlert-icon": {
            color: "#155EEF",
            fontSize: 25,
            mt: "2px",
            p: "5px",
            borderRadius: "50%",
            backgroundColor: "#DCEBFF",
          },
          "& .MuiAlert-message": {
            fontSize: "13px",
            fontWeight: 700,
            lineHeight: 1.55,
            letterSpacing: "0.01em",
          },
          "& .MuiAlert-action": { color: "#344054", pt: "2px" },
        }}
      >
        {toastMessage}
      </Alert>
    </Snackbar>
  );
};

export default NotificationRealtime;
